import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { v4 as uuidv4 } from 'uuid';
import { lookupsJson } from '@ourbranch/lookups';
import { Formik, Form, useFormikContext } from 'formik';
import classNames from 'classnames';

import { FormField, FormSubmit } from 'core/components/form';
import { NotificationCard } from 'core/components/notification-card';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import {
  ENTITY_TYPE_OPTIONS,
  FO41_DESCRIPTION_LABEL,
  FO41_DESCRIPTION_PLACEHOLDER,
  FO41_ELIGIBLE_RELATIONSHIPS,
  getDescriptionLabel,
  getDescriptionPlaceholder,
  getFilteredRelationshipOptions,
  getVisibilityFlags
} from 'common/helpers/farm-interested-party-helpers';
import { getValidationSchema } from './add-farm-interested-party.validation';
import useStyles from '../../people/add-form.styles';

const FormContent = ({
  disabled,
  classes,
  locationOptions,
  equipmentOptions,
  riskOptions,
  sppOptions,
  rpsOptions,
  includeFarmLiability,
  liabilityType,
  existingParties
}) => {
  const { values, setFieldValue } = useFormikContext();
  const { inPolicyRoute } = useRouteHelper();
  const {
    showLocationFields,
    showEquipmentFields,
    showLocationAndRiskFields,
    showLocationRiskAndEquipmentFields,
    showDescriptionOnly,
    showAllFields,
    showResidentInfo
  } = getVisibilityFlags(values.relationship);
  const context = inPolicyRoute ? 'policy' : 'offer';
  const emptyMsg = (itemType) =>
    `There are no ${itemType} on this ${context}. Please add one if you want to associate it to the interested party.`;

  const relationshipOptions = getFilteredRelationshipOptions({
    allOptions: lookupsJson.farm.farmInterestedPartyTypes,
    includeFarmLiability,
    liabilityType,
    existingParties,
    editingPartyId: values.id
  });
  const showFO41Toggle = FO41_ELIGIBLE_RELATIONSHIPS.includes(values.relationship);
  const isBillToOther = values.relationship === 'billToOther';

  // Clear FO-41 fields when switching to a non-eligible relationship
  useEffect(() => {
    if (!showFO41Toggle && values.includeFO41) {
      setFieldValue('includeFO41', false);
    }
  }, [values.relationship]);

  return (
    <Form disabled={disabled} className={classes.container} id="addInterestedParty">
      <Grid
        key="container"
        container
        justifyContent="space-around"
        alignItems="flex-start"
        className={classes.formContainer}
        spacing={4}
      >
        <FormField
          mode="light"
          name="name"
          id="name"
          label="Name or Company"
          type="string"
          xs={12}
          permissions={{ isLicensedAction: false }}
        />
        <FormField
          mode="light"
          name="address"
          id="address"
          label="Address"
          type="address"
          xs={8}
          permissions={{ isLicensedAction: false }}
        />
        <FormField
          mode="light"
          name="address2"
          id="address2"
          label="Address 2"
          type="string"
          optional
          xs={4}
          permissions={{ isLicensedAction: false }}
        />
        <FormField
          mode="light"
          name="city"
          id="city"
          label="City"
          type="string"
          optional
          xs={4}
          permissions={{ isLicensedAction: false }}
        />
        <FormField
          mode="light"
          name="state"
          id="state"
          label="State"
          type="state"
          optional
          xs={4}
          permissions={{ isLicensedAction: false }}
        />
        <FormField
          mode="light"
          name="zip"
          id="zip"
          label="Zip code"
          type="string"
          optional
          xs={4}
          permissions={{ isLicensedAction: false }}
        />
        <FormField
          mode="light"
          name="relationship"
          id="relationship"
          label="Relationship"
          type="select"
          options={relationshipOptions}
          xs={12}
          permissions={{ isLicensedAction: false }}
        />
        {isBillToOther && (
          <FormField
            mode="light"
            name="email"
            id="email"
            label="Email"
            type="string"
            xs={12}
            permissions={{ isLicensedAction: false }}
          />
        )}
        {values.relationship === 'partnerOrCoOwner' && (
          <FormField
            mode="light"
            name="entityType"
            id="entityType"
            label="Entity Type"
            type="select"
            options={ENTITY_TYPE_OPTIONS}
            xs={12}
            permissions={{ isLicensedAction: false }}
          />
        )}
        {values.relationship === 'mortgageeCoOwner' && (
          <FormField
            mode="light"
            name="entityType"
            id="entityType"
            label="Entity type (form of interest)"
            type="select"
            options={lookupsJson.farm.gl108EntityTypeOptions}
            xs={12}
            permissions={{ isLicensedAction: false }}
          />
        )}
        {showLocationFields && (
          <>
            <FormField
              mode="light"
              name="locations"
              id="locations"
              label="Locations"
              description="Select all that apply"
              type="chipArray"
              options={locationOptions}
              emptyMessage={emptyMsg('locations')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="description"
              id="description"
              label={getDescriptionLabel(values.relationship)}
              placeholder={getDescriptionPlaceholder(values.relationship)}
              type="textarea"
              minRows={3}
              maxRows={3}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
          </>
        )}
        {showResidentInfo && (
          <>
            <Grid item xs={12}>
              <NotificationCard className={classes.residentInfoBanner} iconSize="icon16" type="light" noShadow>
                This additional interest/insured is a resident of the household.
              </NotificationCard>
            </Grid>
            <FormField
              mode="light"
              name="description"
              id="description"
              label={getDescriptionLabel(values.relationship)}
              type="textarea"
              minRows={3}
              maxRows={3}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
          </>
        )}
        {showEquipmentFields && (
          <>
            <FormField
              mode="light"
              name="equipment"
              id="equipment"
              label="Associated equipment"
              description="Select all that apply"
              type="chipArray"
              options={equipmentOptions}
              emptyMessage={emptyMsg('equipment')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="description"
              id="description"
              label={getDescriptionLabel(values.relationship)}
              type="textarea"
              minRows={3}
              maxRows={3}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
          </>
        )}
        {showLocationAndRiskFields && (
          <>
            <FormField
              mode="light"
              name="locations"
              id="locations"
              label="Associated locations/properties"
              description="Select all that apply"
              type="chipArray"
              options={locationOptions}
              emptyMessage={emptyMsg('locations')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="risks"
              id="risks"
              label="Associated risks"
              description="Select all that apply"
              type="chipArray"
              options={riskOptions}
              emptyMessage={emptyMsg('risks')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="description"
              id="description"
              label={getDescriptionLabel(values.relationship)}
              type="textarea"
              minRows={3}
              maxRows={3}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
          </>
        )}
        {showLocationRiskAndEquipmentFields && (
          <>
            <FormField
              mode="light"
              name="locations"
              id="locations"
              label="Associated locations/properties"
              description="Select all that apply"
              type="chipArray"
              options={locationOptions}
              emptyMessage={emptyMsg('locations')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="risks"
              id="risks"
              label="Associated risks"
              description="Select all that apply"
              type="chipArray"
              options={riskOptions}
              emptyMessage={emptyMsg('risks')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="equipment"
              id="equipment"
              label="Associated equipment"
              description="Select all that apply"
              type="chipArray"
              options={equipmentOptions}
              emptyMessage={emptyMsg('equipment')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="description"
              id="description"
              label={getDescriptionLabel(values.relationship)}
              type="textarea"
              minRows={3}
              maxRows={3}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
          </>
        )}
        {showDescriptionOnly && (
          <FormField
            mode="light"
            name="description"
            id="description"
            label={getDescriptionLabel(values.relationship)}
            type="textarea"
            minRows={3}
            maxRows={3}
            xs={12}
            permissions={{ isLicensedAction: false }}
          />
        )}
        {showAllFields && (
          <>
            <FormField
              mode="light"
              name="locations"
              id="locations"
              label="Associated locations/properties"
              description="Select all that apply"
              type="chipArray"
              options={locationOptions}
              emptyMessage={emptyMsg('locations')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="risks"
              id="risks"
              label="Associated risks"
              description="Select all that apply"
              type="chipArray"
              options={riskOptions}
              emptyMessage={emptyMsg('risks')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="equipment"
              id="equipment"
              label="Associated equipment"
              description="Select all that apply"
              type="chipArray"
              options={equipmentOptions}
              emptyMessage={emptyMsg('equipment')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="sppItems"
              id="sppItems"
              label="Associated inland marine assets"
              description="Select all that apply"
              type="chipArray"
              options={sppOptions}
              emptyMessage={emptyMsg('inland marine assets')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="rpsItems"
              id="rpsItems"
              label="Associated related private structures"
              description="Select all that apply"
              type="chipArray"
              options={rpsOptions}
              emptyMessage={emptyMsg('related private structures')}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
            <FormField
              mode="light"
              name="description"
              id="description"
              label={getDescriptionLabel(values.relationship)}
              type="textarea"
              minRows={3}
              maxRows={3}
              xs={12}
              permissions={{ isLicensedAction: false }}
            />
          </>
        )}
      </Grid>
      {showFO41Toggle && (
        <Grid container spacing={4} className={classes.fo41Container}>
          <FormField
            mode="light"
            name="includeFO41"
            id="includeFO41"
            label="Additional insureds - Property coverages, FO-41"
            type="switch"
            tooltipLabel="More Info"
            tooltipText="Turn this on when the additional insured has full or partial interest in physical property on the farm. This may include a dwelling, barn, machinery, or other equipment. Once added, select the location and describe the specific property of interest."
            xs={12}
            permissions={{ isLicensedAction: false }}
          />
          {values.includeFO41 && (
            <>
              <FormField
                mode="light"
                name="fo41Locations"
                id="fo41Locations"
                label="Associated locations/properties"
                description="Select all that apply"
                type="chipArray"
                options={locationOptions}
                emptyMessage={emptyMsg('locations')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name="fo41Risks"
                id="fo41Risks"
                label="Associated risks"
                description="Select all that apply"
                type="chipArray"
                options={riskOptions}
                emptyMessage={emptyMsg('risks')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name="fo41Equipment"
                id="fo41Equipment"
                label="Associated equipment"
                description="Select all that apply"
                type="chipArray"
                options={equipmentOptions}
                emptyMessage={emptyMsg('equipment')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name="fo41SppItems"
                id="fo41SppItems"
                label="Associated inland marine assets"
                description="Select all that apply"
                type="chipArray"
                options={sppOptions}
                emptyMessage={emptyMsg('inland marine assets')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name="fo41RpsItems"
                id="fo41RpsItems"
                label="Associated related private structures"
                description="Select all that apply"
                type="chipArray"
                options={rpsOptions}
                emptyMessage={emptyMsg('related private structures')}
                xs={12}
                permissions={{ isLicensedAction: false }}
              />
              <FormField
                mode="light"
                name="fo41Description"
                id="fo41Description"
                label={FO41_DESCRIPTION_LABEL}
                placeholder={FO41_DESCRIPTION_PLACEHOLDER}
                type="textarea"
                minRows={3}
                maxRows={3}
                xs={12}
                className={classes.fo41Description}
                permissions={{ isLicensedAction: false }}
              />
            </>
          )}
        </Grid>
      )}
      <Grid container justifyContent="flex-end" alignItems="center" spacing={4}>
        <FormSubmit
          mode="big"
          className={classNames(classes.button, classes.addButton)}
          variant="contained"
          color="secondary"
          xs={4}
        >
          Add Farm Interested Party
        </FormSubmit>
      </Grid>
    </Form>
  );
};

function AddFarmInterestedPartyForm({
  onAdd,
  disabled,
  locationOptions,
  equipmentOptions,
  riskOptions,
  sppOptions,
  rpsOptions,
  isPolicy,
  includeFarmLiability,
  liabilityType,
  existingParties
}) {
  const classes = useStyles();

  return (
    <Formik
      initialValues={{
        id: uuidv4(),
        name: undefined,
        email: undefined,
        address: undefined,
        relationship: undefined,
        address2: undefined,
        city: undefined,
        state: undefined,
        zip: undefined,
        locations: [],
        equipment: [],
        risks: [],
        sppItems: [],
        rpsItems: [],
        description: undefined,
        isResident: false,
        entityType: undefined,
        includeFO41: false,
        fo41Locations: [],
        fo41Risks: [],
        fo41Equipment: [],
        fo41SppItems: [],
        fo41RpsItems: [],
        fo41Description: undefined
      }}
      validationSchema={getValidationSchema(isPolicy)}
      onSubmit={({
        id,
        name,
        email,
        relationship,
        address,
        address2,
        city,
        state,
        zip,
        locations,
        equipment,
        risks,
        sppItems,
        rpsItems,
        description,
        isResident,
        entityType,
        includeFO41,
        fo41Locations,
        fo41Risks,
        fo41Equipment,
        fo41SppItems,
        fo41RpsItems,
        fo41Description
      }) =>
        onAdd({
          id,
          name,
          email,
          relationship,
          address: { address, address2, city, state, zip },
          locations,
          risks,
          equipment,
          sppItems,
          rpsItems,
          description,
          isResident,
          entityType,
          includeFO41,
          fo41Locations,
          fo41Risks,
          fo41Equipment,
          fo41SppItems,
          fo41RpsItems,
          fo41Description
        })
      }
    >
      {() => (
        <FormContent
          disabled={disabled}
          classes={classes}
          locationOptions={locationOptions}
          equipmentOptions={equipmentOptions}
          riskOptions={riskOptions}
          sppOptions={sppOptions}
          rpsOptions={rpsOptions}
          includeFarmLiability={includeFarmLiability}
          liabilityType={liabilityType}
          existingParties={existingParties}
        />
      )}
    </Formik>
  );
}

FormContent.propTypes = {
  disabled: PropTypes.bool,
  classes: PropTypes.object.isRequired,
  locationOptions: PropTypes.array,
  equipmentOptions: PropTypes.array,
  riskOptions: PropTypes.array,
  sppOptions: PropTypes.array,
  rpsOptions: PropTypes.array,
  includeFarmLiability: PropTypes.bool,
  liabilityType: PropTypes.string,
  existingParties: PropTypes.array
};

FormContent.defaultProps = {
  disabled: false,
  locationOptions: [],
  equipmentOptions: [],
  riskOptions: [],
  sppOptions: [],
  rpsOptions: [],
  includeFarmLiability: false,
  liabilityType: undefined,
  existingParties: []
};

AddFarmInterestedPartyForm.propTypes = {
  onAdd: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  locationOptions: PropTypes.array,
  equipmentOptions: PropTypes.array,
  riskOptions: PropTypes.array,
  sppOptions: PropTypes.array,
  rpsOptions: PropTypes.array,
  isPolicy: PropTypes.bool,
  includeFarmLiability: PropTypes.bool,
  liabilityType: PropTypes.string,
  existingParties: PropTypes.array
};
AddFarmInterestedPartyForm.defaultProps = {
  disabled: false,
  locationOptions: [],
  equipmentOptions: [],
  riskOptions: [],
  sppOptions: [],
  rpsOptions: [],
  isPolicy: false,
  includeFarmLiability: false,
  liabilityType: undefined,
  existingParties: []
};

export default AddFarmInterestedPartyForm;
